import type { FillLayerSpecification, LayerSpecification } from 'mapbox-gl';
import type { BaseStyleId } from '@alltrails/maps/types/Styles';

export type FallFoliageWeek = string;
export type FallFoliageLightPreset = 'day' | 'night';

export const fallFoliageDefaultWeek: FallFoliageWeek = '1';
export const fallFoliageDefaultMinScore = 1;
export const fallFoliageSourceId = 'fall-foliage';
export const fallFoliagePlaybackIntervalMs = 750;

export type FallFoliageConfig = {
  lightPreset: FallFoliageLightPreset;
  minScore: number;
  visible: boolean;
  week: FallFoliageWeek;
};

export const fallFoliageDefaultConfig: FallFoliageConfig = {
  lightPreset: 'day',
  minScore: fallFoliageDefaultMinScore,
  visible: true,
  week: fallFoliageDefaultWeek
};

export type FallFoliageStyleSource = {
  attribution?: string;
  maxzoom?: number;
  minzoom?: number;
  promoteId?: string;
  tiles: string[];
  type: 'vector';
};

export type FallFoliageStyleJsonSource = {
  attribution?: string;
  maxzoom?: number;
  minzoom?: number;
  promoteId?: string;
  tiles?: string[];
  type?: string;
};

export type FallFoliageStyleJsonSchema = {
  week?: {
    default?: unknown;
    values?: unknown;
  };
};

export type FallFoliageStyleJson = {
  layers?: LayerSpecification[];
  schema?: FallFoliageStyleJsonSchema;
  sources?: Record<string, FallFoliageStyleJsonSource>;
};

export type FallFoliageStyle = {
  defaultWeek: FallFoliageWeek | null;
  layers: LayerSpecification[];
  source: FallFoliageStyleSource;
  weeks: FallFoliageWeek[];
};

/** The forecast horizon is owned by the style JSON, so the week options come from its import schema. */
export function parseFallFoliageWeeks(schema: FallFoliageStyleJsonSchema | undefined): FallFoliageWeek[] {
  const values = schema?.week?.values;
  if (!Array.isArray(values)) {
    return [];
  }
  return values.filter((value): value is string => typeof value === 'string' && value.length > 0);
}

/** The week to fall back on when the selected week is not offered by the style JSON. */
export function parseFallFoliageDefaultWeek(schema: FallFoliageStyleJsonSchema | undefined, weeks: FallFoliageWeek[]): FallFoliageWeek | null {
  const schemaDefault = schema?.week?.default;
  if (typeof schemaDefault === 'string' && weeks.includes(schemaDefault)) {
    return schemaDefault;
  }
  return weeks[0] ?? null;
}

export function getFallFoliageLightPreset(baseStyleId: BaseStyleId): FallFoliageLightPreset {
  return baseStyleId === 'alltrailsSatelliteV2' || baseStyleId === 'googleSatellite' ? 'night' : 'day';
}

function appendTileApiKey(url: string, apiKey: string): string {
  if (!apiKey || /[?&]key=/.test(url)) {
    return url;
  }
  return `${url}${url.includes('?') ? '&' : '?'}key=${apiKey}`;
}

export function parseFallFoliageVectorSource(
  sources: Record<string, FallFoliageStyleJsonSource> | undefined,
  apiKey: string
): FallFoliageStyleSource | null {
  const source = sources?.[fallFoliageSourceId];
  const tileUrl = source?.tiles?.[0];
  if (!source || source.type !== 'vector' || !tileUrl) {
    return null;
  }

  return {
    type: 'vector',
    tiles: [appendTileApiKey(tileUrl, apiKey)],
    ...(source.attribution !== undefined ? { attribution: source.attribution } : {}),
    ...(source.maxzoom !== undefined ? { maxzoom: source.maxzoom } : {}),
    ...(source.minzoom !== undefined ? { minzoom: source.minzoom } : {}),
    ...(typeof source.promoteId === 'string' ? { promoteId: source.promoteId } : {})
  };
}

/** Turns the style JSON into the pieces the overlay and the legend need, throwing if it cannot render. */
export function parseFallFoliageStyle(styleJson: FallFoliageStyleJson, apiKey: string): FallFoliageStyle {
  const source = parseFallFoliageVectorSource(styleJson.sources, apiKey);
  if (!source || !styleJson.layers?.length) {
    throw new Error('Fall foliage style is missing source or layers');
  }

  const weeks = parseFallFoliageWeeks(styleJson.schema);
  return { defaultWeek: parseFallFoliageDefaultWeek(styleJson.schema, weeks), layers: styleJson.layers, source, weeks };
}

function resolveConfigValue(value: unknown, config: FallFoliageConfig): unknown {
  if (Array.isArray(value)) {
    if (value.length >= 2 && value[0] === 'config' && typeof value[1] === 'string') {
      const key = value[1];
      if (key === 'week' || key === 'minScore' || key === 'visible' || key === 'lightPreset') {
        return config[key];
      }
    }
    return value.map(child => resolveConfigValue(child, config));
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, resolveConfigValue(child, config)]));
  }
  return value;
}

/** Replace Mapbox import-schema `["config", ...]` expressions and drop `slot` so layers can be added via `<Layer />`. */
export function resolveFallFoliageConfigExpressions(
  layers: LayerSpecification[],
  config: FallFoliageConfig = fallFoliageDefaultConfig
): LayerSpecification[] {
  return layers.map(layer => {
    const resolved = resolveConfigValue(layer, config) as LayerSpecification & { slot?: string };
    const { slot: _slot, ...rest } = resolved;
    return rest;
  });
}

function isEqualsTrue(condition: unknown): boolean {
  if (condition === true) {
    return true;
  }
  return Array.isArray(condition) && condition[0] === '==' && condition.length === 3 && condition[1] === condition[2];
}

/**
 * Walk nested `case` expressions to the branch the legend should show. Conditions that are not
 * statically true (data-driven ones like `is_past_peak_week`) fall through to the default branch.
 */
function pickCaseBranch(expression: unknown): unknown {
  if (!Array.isArray(expression) || expression[0] !== 'case') {
    return expression;
  }

  for (let index = 1; index < expression.length - 1; index += 2) {
    if (isEqualsTrue(expression[index])) {
      return pickCaseBranch(expression[index + 1]);
    }
  }

  return pickCaseBranch(expression[expression.length - 1]);
}

function extractInterpolateColorStops(expression: unknown): [number, string][] {
  if (!Array.isArray(expression) || expression[0] !== 'interpolate') {
    return [];
  }

  const stops: [number, string][] = [];
  for (let index = 3; index < expression.length - 1; index += 2) {
    const score = expression[index];
    const color = expression[index + 1];
    if (typeof score === 'number' && typeof color === 'string') {
      stops.push([score, color]);
    }
  }
  return stops;
}

/** Pull fill-color stops from the first fill layer so the legend can match the style JSON palette. */
export function extractFallFoliageLegendColorStops(layers: LayerSpecification[]): [number, string][] {
  const fillLayer = layers.find((layer): layer is FillLayerSpecification => layer.type === 'fill');
  return extractInterpolateColorStops(pickCaseBranch(fillLayer?.paint?.['fill-color']));
}

/** CSS linear-gradient matching style-JSON fill-color stops, scaled 0–100% across the score range. */
export function buildFallFoliageLegendGradient(stops: [number, string][]): string {
  if (stops.length === 0) {
    return '';
  }

  const min = stops[0][0];
  const max = stops[stops.length - 1][0];
  const range = max - min || 1;
  const cssStops = stops.map(([score, hex]) => `${hex} ${((score - min) / range) * 100}%`);
  return `linear-gradient(to right, ${cssStops.join(', ')})`;
}
